import { Component } from '@theme/component';

const YT_PLAYING = 1;
const POLL_INTERVAL_MS = 1000;
// Must match the opacity transition duration on .shoppable-video__live in custom-shoppable-video.liquid
const FADE_TRANSITION_MS = 600;

const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');

/** @type {Promise<any> | undefined} */
let ytApiPromise;

/**
 * Loads the YouTube IFrame Player API once, sharing the same promise across every instance on the page.
 * @returns {Promise<any>}
 */
function loadYouTubeIframeAPI() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;

  ytApiPromise = new Promise((resolve) => {
    const previousCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousCallback === 'function') previousCallback();
      resolve(window.YT);
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(script);
    }
  });

  return ytApiPromise;
}

/**
 * @typedef {{ id: string, start: number, end: number }} ShoppableOutfit
 */

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} [player]
 * @property {HTMLScriptElement} [outfitsData]
 * @property {HTMLElement} [tabLive]
 * @property {HTMLElement} [tabAll]
 * @property {HTMLElement} [livePanelWrapper]
 * @property {HTMLElement} [allPanelWrapper]
 * @property {HTMLElement[]} [livePanel]
 * @property {HTMLElement[]} [headerOutfitTitle]
 * @property {HTMLElement[]} [headerOutfitTitleWrapper]
 * @property {HTMLButtonElement} [posterButton]
 * @property {HTMLElement} [screenshotsWrap]
 * @property {HTMLElement} [screenshotsSwiper]
 * @property {HTMLElement[]} [screenshotSlide]
 * @property {HTMLButtonElement} [screenshotsPrev]
 * @property {HTMLButtonElement} [screenshotsNext]
 *
 * @extends {Component<Refs>}
 */
class ShoppableVideoComponent extends Component {
  /** @type {ShoppableOutfit[]} */
  #outfits = [];
  /** @type {string | null} */
  #activeOutfitId = null;
  /** @type {'live' | 'all'} */
  #mode = 'live';
  /** @type {any} */
  #player = null;
  /** @type {number | undefined} */
  #rafId;
  /** @type {number | undefined} */
  #intervalId;
  /** @type {number | null} */
  #startSeconds = null;
  /** @type {any} */
  #screenshotsSwiper = null;

  connectedCallback() {
    super.connectedCallback();

    this.#outfits = this.#parseOutfits();
    this.#startSeconds = this.#getStartSeconds();
    this.#initScreenshotsSwiper();

    const player = this.refs.player;
    if (!player) return;

    const youtubeId = player.dataset.youtubeId;
    if (!youtubeId) return;

    if (this.#startSeconds !== null) {
      // A ?started_at=<minutes>.<seconds> link is a direct deep link to a moment in
      // the video — skip the poster/click-to-play gate and jump straight there.
      this.#initPlayer(player, youtubeId, true);
    } else if (this.refs.posterButton) {
      this.refs.posterButton.addEventListener('click', () => this.#initPlayer(player, youtubeId, true), {
        once: true,
      });
    } else {
      this.#initPlayer(player, youtubeId, false);
    }
  }

  /**
   * Sets up the Swiper carousel for the outfit screenshots shown under the video, if present.
   */
  #initScreenshotsSwiper() {
    const el = this.refs.screenshotsSwiper;
    if (!el || typeof Swiper === 'undefined') return;

    this.#screenshotsSwiper = new Swiper(el, {
      slidesPerView: 5.5,
      spaceBetween: 2.5,
      freeMode: true,
      watchOverflow: true,
      // Swiper installs its own capturing click listener on the swiper container to swallow
      // the click that follows a drag (preventClicks/preventClicksPropagation, both true by
      // default) — this intercepts every tap on a slide before it ever reaches our own click
      // handling, not just ones following an actual drag. Disable both so taps on the
      // screenshot buttons always register.
      preventClicks: false,
      preventClicksPropagation: false,
      navigation: {
        prevEl: this.refs.screenshotsPrev ?? null,
        nextEl: this.refs.screenshotsNext ?? null,
      },
      // Matches this theme's `md` breakpoint (assets/tailwind.css)
      breakpoints: {
        749: {
          slidesPerView: 7.5,
          spaceBetween: 8,
        },
      },
    });
  }

  /**
   * Reads `?started_at=<minutes>.<seconds>` from the current URL (e.g. `5.53` for
   * 5 minutes 53 seconds — the part after the dot is literal seconds, not a decimal
   * fraction of a minute) and returns the total seconds to seek to, or null if the
   * param is absent or malformed.
   * @returns {number | null}
   */
  #getStartSeconds() {
    const raw = new URLSearchParams(window.location.search).get('started_at');
    if (!raw) return null;

    const [minutesPart, secondsPart = '0'] = raw.split('.');
    const minutes = Number.parseInt(minutesPart, 10);
    const seconds = Number.parseInt(secondsPart, 10);

    if (Number.isNaN(minutes) || Number.isNaN(seconds)) return null;

    return minutes * 60 + seconds;
  }

  /**
   * @param {HTMLElement} player
   * @param {string} youtubeId
   * @param {boolean} autoplay
   */
  #initPlayer(player, youtubeId, autoplay) {
    this.refs.posterButton?.remove();

    loadYouTubeIframeAPI().then((YT) => {
      if (!this.isConnected) return;

      const playerVars = {
        rel: 0,
        playsinline: 1,
        autoplay: autoplay ? 1 : 0,
        controls: 1,
        modestbranding: 1,
        disablekb: 1,
        fs: 0,
        iv_load_policy: 3,
        cc_load_policy: 0,
      };

      if (this.#startSeconds !== null) {
        playerVars.start = this.#startSeconds;
      }

      this.#player = new YT.Player(player.id, {
        videoId: youtubeId,
        playerVars,
        events: {
          onReady: () => {
            if (this.#startSeconds !== null) {
              // `start` only cues the initial load; seekTo covers players that ignore it
              // (e.g. already-buffered embeds) so the deep link is reliable either way.
              // getCurrentTime() won't reflect the seek right away, so don't sync from it
              // yet — the poll below will pick up the right outfit once it does, and
              // syncing now would flash whichever outfit spans time 0.
              this.#player.seekTo(this.#startSeconds, true);
            } else {
              this.#syncActiveOutfit();
            }
          },
          onStateChange: (/** @type {{ data: number }} */ event) => this.#handleStateChange(event),
        },
      });
    });

    this.#intervalId = window.setInterval(() => this.#syncActiveOutfit(), POLL_INTERVAL_MS);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopPolling();
    if (this.#intervalId) window.clearInterval(this.#intervalId);
    this.#player?.destroy?.();
    this.#screenshotsSwiper?.destroy?.(true, true);
  }

  /**
   * Handles a click on one of the outfit screenshots under the video: jumps the player to
   * that outfit's start time (initializing the player first if it hasn't started yet) and
   * plays from there.
   * @param {{ outfitId: string }} data
   */
  selectOutfitScreenshot({ outfitId }) {
    const outfit = this.#outfits.find((candidate) => candidate.id === String(outfitId));
    if (!outfit) return;

    this.#activeOutfitId = outfit.id;
    this.#renderActiveOutfit(outfit.id);

    if (this.#player && typeof this.#player.seekTo === 'function') {
      this.#player.seekTo(outfit.start, true);
      this.#player.playVideo?.();
      return;
    }

    const player = this.refs.player;
    if (!player) return;

    const youtubeId = player.dataset.youtubeId;
    if (!youtubeId) return;

    this.#startSeconds = outfit.start;
    this.#initPlayer(player, youtubeId, true);
  }

  /** @returns {ShoppableOutfit[]} */
  #parseOutfits() {
    const raw = this.refs.outfitsData?.textContent;
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.sort((a, b) => a.start - b.start) : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /** @param {{ data: number }} event */
  #handleStateChange(event) {
    if (event.data === YT_PLAYING) {
      this.#startPolling();
    } else {
      this.#stopPolling();
    }

    this.#syncActiveOutfit();
  }

  #startPolling() {
    if (this.#rafId) return;

    const tick = () => {
      this.#syncActiveOutfit();
      this.#rafId = requestAnimationFrame(tick);
    };

    this.#rafId = requestAnimationFrame(tick);
  }

  #stopPolling() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = undefined;
  }

  #syncActiveOutfit() {
    if (!this.#player || typeof this.#player.getCurrentTime !== 'function') return;

    const time = this.#player.getCurrentTime();
    const outfit = this.#outfits.find((candidate) => time >= candidate.start && time < candidate.end);
    const outfitId = outfit ? outfit.id : null;

    if (outfitId === this.#activeOutfitId) return;

    this.#activeOutfitId = outfitId;
    this.#renderActiveOutfit(outfitId);
  }

  /** @param {string | null} outfitId */
  #renderActiveOutfit(outfitId) {
    if (outfitId) this.#scrollScreenshotsToOutfit(outfitId);

    const apply = () => {
      for (const panel of this.refs.livePanel ?? []) {
        panel.hidden = panel.dataset.outfitId !== outfitId;
      }

      for (const heading of this.refs.headerOutfitTitle ?? []) {
        heading.hidden = heading.dataset.outfitId !== outfitId;
      }

      for (const slide of this.refs.screenshotSlide ?? []) {
        slide.classList.toggle('is-active', slide.dataset.outfitId === outfitId);
      }
    };

    const wrapper = this.refs.livePanelWrapper;

    if (REDUCED_MOTION.matches || !wrapper) {
      apply();
      return;
    }

    // Fade the current outfit out, swap the visible panel while hidden, then let the CSS
    // transition on .shoppable-video__live fade the new outfit back in.
    wrapper.classList.add('is-fading');

    window.setTimeout(() => {
      apply();
      wrapper.classList.remove('is-fading');
    }, FADE_TRANSITION_MS);
  }

  /**
   * Scrolls the outfit screenshots swiper so the slide for the given outfit is back in view —
   * e.g. if playback jumps from outfit 12 back to outfit 2, the swiper (which may have scrolled
   * forward to show outfit 12) returns to where outfit 2 is, rather than staying scrolled ahead.
   * @param {string} outfitId
   */
  #scrollScreenshotsToOutfit(outfitId) {
    if (!this.#screenshotsSwiper) return;

    const index = (this.refs.screenshotSlide ?? []).findIndex((slide) => slide.dataset.outfitId === outfitId);
    if (index === -1) return;

    this.#screenshotsSwiper.slideTo(index);
  }

  showLive() {
    this.#setMode('live');
  }

  showAll() {
    this.#setMode('all');
  }

  /** @param {'live' | 'all'} mode */
  #setMode(mode) {
    if (mode === this.#mode) return;
    this.#mode = mode;

    this.refs.tabLive?.classList.toggle('is-active', mode === 'live');
    this.refs.tabAll?.classList.toggle('is-active', mode === 'all');
    this.refs.tabLive?.setAttribute('aria-selected', String(mode === 'live'));
    this.refs.tabAll?.setAttribute('aria-selected', String(mode === 'all'));

    // Switch the outfit title to white instead of hiding it, so the header keeps the same
    // height and nothing else shifts when "View All Outfits" is selected.
    for (const wrapper of this.refs.headerOutfitTitleWrapper ?? []) {
      wrapper.classList.toggle('is-all-mode', mode === 'all');
    }

    const liveWrapper = this.refs.livePanelWrapper;
    const allWrapper = this.refs.allPanelWrapper;

    const apply = () => {
      if (liveWrapper) liveWrapper.hidden = mode !== 'live';
      if (allWrapper) allWrapper.hidden = mode !== 'all';
      if (this.refs.screenshotsWrap) this.refs.screenshotsWrap.hidden = mode !== 'live';
    };

    if (REDUCED_MOTION.matches || (!liveWrapper && !allWrapper)) {
      apply();
      return;
    }

    // Fade the current panel out, swap which one is visible while hidden, then let the CSS
    // transition fade the new panel back in — same pattern as #renderActiveOutfit.
    liveWrapper?.classList.add('is-fading');
    allWrapper?.classList.add('is-fading');

    window.setTimeout(() => {
      apply();
      liveWrapper?.classList.remove('is-fading');
      allWrapper?.classList.remove('is-fading');
    }, FADE_TRANSITION_MS);
  }
}

if (!customElements.get('shoppable-video-component')) {
  customElements.define('shoppable-video-component', ShoppableVideoComponent);
}
